import { Controller } from "@hotwired/stimulus"

// Tracks completion state for each required section of the campaign form.
// Shows a per-section ✓ badge once required fields are filled,
// and an overall "ready" indicator in the actions card when all sections pass.
export default class extends Controller {
  static targets = ["setupBadge", "accessBadge", "stepsBadge", "overallBadge"]

  connect() {
    // Inject animation keyframe once
    if (!document.getElementById("fp-styles")) {
      const s = document.createElement("style")
      s.id = "fp-styles"
      s.textContent = `
        @keyframes fpFadeUp {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fp-visible { animation: fpFadeUp 0.22s cubic-bezier(0.16,1,0.3,1) both; }
      `
      document.head.appendChild(s)
    }

    // Watch steps container for dynamically added/removed rows
    const stepsContainer = this.element.querySelector("[data-test-steps-target=\"container\"]")
    if (stepsContainer) {
      this._observer = new MutationObserver(() => this._checkSteps())
      this._observer.observe(stepsContainer, { childList: true, subtree: true })
      stepsContainer.addEventListener("input", () => this._checkSteps())
    }

    this._checkAll()
  }

  disconnect() {
    this._observer?.disconnect()
  }

  // Called via data-action on the campaign name input
  checkSetup() { this._checkSetup() }

  // Called via data-action on the test URL input
  checkAccess() { this._checkAccess() }

  // ── Private ──────────────────────────────────────────────

  _checkSetup() {
    const input = this.element.querySelector("#test_campaign_name")
    this._setBadge(this.setupBadgeTarget, !!(input?.value.trim()))
    this._updateOverall()
  }

  _checkAccess() {
    const input = this.element.querySelector("#test_campaign_test_url")
    this._setBadge(this.accessBadgeTarget, !!(input?.value.trim()))
    this._updateOverall()
  }

  _checkSteps() {
    const container = this.element.querySelector("[data-test-steps-target=\"container\"]")
    if (!container) {
      this._setBadge(this.stepsBadgeTarget, false)
      this._updateOverall()
      return
    }
    const rows = container.querySelectorAll(".step-row:not([style*=\"display: none\"])")
    const filledTitles = Array.from(rows).filter(row => {
      const inp = row.querySelector("input[name*=\"[title]\"]")
      return inp?.value.trim().length > 0
    }).length
    this._setBadge(this.stepsBadgeTarget, filledTitles >= 2)
    this._updateOverall()
  }

  _checkAll() {
    this._checkSetup()
    this._checkAccess()
    this._checkSteps()
  }

  _setBadge(target, isComplete) {
    if (isComplete) {
      target.style.display = "flex"
      // Re-trigger animation by toggling the class
      target.classList.remove("fp-visible")
      void target.offsetWidth // force reflow
      target.classList.add("fp-visible")
    } else {
      target.style.display = "none"
      target.classList.remove("fp-visible")
    }
  }

  _updateOverall() {
    if (!this.hasOverallBadgeTarget) return
    const allDone = [this.setupBadgeTarget, this.accessBadgeTarget, this.stepsBadgeTarget]
      .every(t => t.style.display !== "none")
    this._setBadge(this.overallBadgeTarget, allDone)
  }
}
